import React, { useState, useEffect } from 'react';
import { Dropdown } from '../UI/V3/Dropdown/Dropdown';
import { DropdownTrigger } from '../UI/V3/Dropdown/Trigger/DropdownTrigger';
import { DropdownContent } from '../UI/V3/Dropdown/Content/DropdownContent';
import { DropdownItem } from '../UI/V3/Dropdown/Item/DropdownItem';
import { DropdownFooter } from '../UI/V3/Dropdown/Footer/DropdownFooter';
import Button from '../UI/V3/Button/Button';
import { faStore } from '@fortawesome/pro-regular-svg-icons';
import { useMediaQuery } from 'react-responsive';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { useOrganizationsContext } from './OrganizationsProvider';
import styles from './BusinessTypeFilterDropdown.module.scss';

interface BusinessType {
    value: string;
    label: string;
    isAllOption?: boolean;
}

export const BusinessTypeFilterDropdown: React.FC = () => {
    const { selectedBusinessTypes, setSelectedBusinessTypes } = useOrganizationsContext();

    const [isOpen, setIsOpen] = useState(false);
    const [tempSelectedTypes, setTempSelectedTypes] = useState<string[]>(selectedBusinessTypes);
    const isMobile = useMediaQuery({ query: '(max-width:600px)' });

    const { businessTypes } = useSelector((state: RootState) => state.masterData.data);

    const businessTypeOptions = businessTypes.map((type: { id: string; name: string }) => ({
        label: type.name,
        value: type.id
    }));
    const businessTypesNames = businessTypes.map((type: { id: string; name: string }) => type.name.toLowerCase());

    const BUSINESS_TYPES: BusinessType[] = [
        { value: 'all', label: 'All Business Types', isAllOption: true },
        ...businessTypeOptions
    ];

    // Sync temp state when dropdown opens
    useEffect(() => {
        if (isOpen) {
            setTempSelectedTypes(selectedBusinessTypes);
        }
    }, [isOpen, selectedBusinessTypes]);

    // Determine button label based on selected types
    const getButtonLabel = (): string => {
        if (selectedBusinessTypes.length === 0 || selectedBusinessTypes.length === 2) {
            return 'All types';
        }
        if (selectedBusinessTypes.length === 1) {
            // Capitalize first letter for display
            const type = selectedBusinessTypes[0];
            return type.charAt(0).toUpperCase() + type.slice(1);
        }
        // Capitalize first letter of each type for display
        return selectedBusinessTypes.map(t => t.charAt(0).toUpperCase() + t.slice(1)).join(', ');
    };

    // Check if all types are selected
    const isAllTypesSelected = tempSelectedTypes.length === 2;

    // Handle "All Business Types" checkbox toggle
    const handleAllTypesToggle = (selected: boolean): void => {
        if (selected) {
            setTempSelectedTypes(businessTypesNames);
        } else {
            setTempSelectedTypes([]);
        }
    };

    // Handle individual type checkbox toggle
    const handleTypeToggle = (type: string, selected: boolean): void => {
        if (selected) {
            const newTypes = [...tempSelectedTypes, type];
            setTempSelectedTypes(newTypes);
        } else {
            const newTypes = tempSelectedTypes.filter(t => t !== type);
            setTempSelectedTypes(newTypes);
        }
    };

    // Handle Apply button click
    const handleApply = (): void => {
        setSelectedBusinessTypes(tempSelectedTypes);
        setIsOpen(false);
    };

    return (
        <Dropdown
            open={isOpen}
            onOpenChange={setIsOpen}
        >
            <DropdownTrigger>
                <Button
                    label={isMobile ? undefined : getButtonLabel()}
                    variant="outlined"
                    size="large"
                    leftIcon={faStore}
                    className={isMobile ? styles.iconOnlyButton : styles.defaultButton}
                />
            </DropdownTrigger>

            <DropdownContent
                align="end"
                className={styles.dropdownContent}
            >
                {BUSINESS_TYPES.map(type => (
                    <DropdownItem
                        key={type.value}
                        type="checkbox"
                        checked={type.isAllOption ? isAllTypesSelected : tempSelectedTypes.includes(type.value)}
                        onSelect={selected =>
                            type.isAllOption ? handleAllTypesToggle(selected) : handleTypeToggle(type.value, selected)
                        }
                        className={type.isAllOption ? styles.itemWithSeparator : undefined}
                    >
                        {type.label}
                    </DropdownItem>
                ))}

                <DropdownFooter className={styles.dropdownFooter}>
                    <Button
                        label="Apply"
                        onClick={handleApply}
                        fullWidth
                        variant="primary"
                    />
                </DropdownFooter>
            </DropdownContent>
        </Dropdown>
    );
};

export default BusinessTypeFilterDropdown;
